/**
 * Eden Treaty client for the Portal API.
 *
 * Default auth: `credentials: 'include'` sends httpOnly session cookies with
 * every request (Better Auth manages sessions).
 *
 * Embed-route auth: when the app is rendered on a /embed/* path, the fetcher
 * swaps cookies for `Authorization: Bearer <PUBLIC_DEMO_PAT>`. This lets the
 * live dashboard embeds on the marketing page render against the demo org
 * without requiring a user session.
 *
 * A global onResponse hook intercepts 401 responses: clears the session and
 * redirects to /login so the user doesn't see silent failures on expired
 * tokens. Redirects are suppressed on embed routes.
 *
 * fetchWithResilience wraps every request with:
 *   - Offline detection: shows a toast and rejects immediately if navigator.onLine is false
 *   - 30s AbortController timeout per attempt
 *   - Up to 2 retries for transient failures (502/503/504 and network errors)
 */
import { createApiClient } from '@solidafy/api-client';
import { config } from './config';
import { toast } from '@/lib/toast';

let redirecting = false;

const TIMEOUT_MS = 30_000;
const MAX_RETRIES = 2;
const RETRYABLE_STATUSES = new Set([502, 503, 504]);

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Resolve the token used to authenticate /embed/* requests, in priority order:
 *   1. the build-baked PUBLIC_DEMO_PAT (config.demoPat), then
 *   2. a `?demo_token=<pat>` URL param, stashed in sessionStorage so it
 *      survives client-side navigation and query-string changes.
 *
 * The URL fallback lets a marketing page drive embeds against an environment
 * (e.g. staging) without baking a PAT into the build. Only ever consulted on
 * /embed/* routes, so it can't leak onto authenticated app requests.
 */
export function getEmbedToken(): string | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  if (!window.location.pathname.startsWith('/embed/')) {
    return undefined;
  }
  // A ?demo_token in the URL WINS over the build-baked PUBLIC_DEMO_PAT. A
  // marketing page that pins a token means it: it can point at a specific
  // (e.g. staging) demo org regardless of whether PUBLIC_DEMO_PAT is unset or
  // stale in the deployed environment. Falls back to the build PAT otherwise.
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('demo_token');
    if (fromUrl) {
      return fromUrl;
    }
  } catch {
    /* sessionStorage / URL parse unavailable — fall through to the build PAT */
  }
  return config.demoPat;
}

/**
 * When the app is rendered on a /embed/* route, swap cookie auth for a Bearer
 * token (see getEmbedToken for where it comes from). This lets an
 * unauthenticated visitor see the demo-org dashboards without a session.
 */
function applyEmbedAuth(init: RequestInit | undefined): RequestInit | undefined {
  if (typeof window === 'undefined') {
    return init;
  }
  const token = getEmbedToken();
  if (!token) {
    return init;
  }

  const headers = new Headers(init?.headers);
  headers.set('Authorization', `Bearer ${token}`);

  return {
    ...init,
    headers,
    // Do NOT send cookies on embed requests — we rely solely on the PAT so
    // the embed behaves identically for a logged-in and a logged-out visitor.
    credentials: 'omit' as RequestCredentials,
  };
}

export async function fetchWithResilience(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  if (!navigator.onLine) {
    toast.error('You appear to be offline — check your connection');
    throw new TypeError('Failed to fetch: offline');
  }

  const effectiveInit = applyEmbedAuth(init);
  const callerSignal = effectiveInit?.signal ?? null;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

    // Wire the caller's signal into the timeout controller so both sources of
    // cancellation abort the in-flight request.
    const onCallerAbort = () => controller.abort(callerSignal!.reason);
    callerSignal?.addEventListener('abort', onCallerAbort);

    try {
      // If the caller already aborted before we started, throw without calling fetch.
      if (callerSignal?.aborted) {
        throw callerSignal.reason ?? new DOMException('The operation was aborted.', 'AbortError');
      }

      const response = await fetch(input, { ...effectiveInit, signal: controller.signal });

      if (RETRYABLE_STATUSES.has(response.status) && attempt < MAX_RETRIES) {
        await response.body?.cancel();
        await sleep(500 * (attempt + 1));
        continue;
      }

      return response;
    } catch (err) {
      // Caller explicitly cancelled — do not retry.
      if (callerSignal?.aborted) {
        throw err;
      }
      // Only retry on expected transient failures: network errors (TypeError) and
      // our own timeout AbortError. Rethrow everything else immediately so
      // programmer errors and unexpected exceptions are not silently swallowed.
      const isNetworkError = err instanceof TypeError;
      const isTimeoutAbort = err instanceof DOMException && err.name === 'AbortError';
      if ((isNetworkError || isTimeoutAbort) && attempt < MAX_RETRIES) {
        await sleep(500 * (attempt + 1));
        continue;
      }
      throw err;
    } finally {
      clearTimeout(timeoutId);
      callerSignal?.removeEventListener('abort', onCallerAbort);
    }
  }

  // Unreachable — loop always returns or throws within the retry bound
  throw new Error('unreachable');
}

export const api = createApiClient(config.apiUrl, {
  fetch: { credentials: 'include' },
  fetcher: fetchWithResilience as typeof fetch,
  // Disable Eden Treaty's auto Date coercion — query results contain ISO timestamps
  // that should stay as strings. Date objects cause {0 keys} in table rendering.
  parseDate: false,
  onResponse: (response) => {
    if (response.status === 401 && !redirecting) {
      // Skip if already on the login page
      if (window.location.pathname.startsWith('/login')) {
        return;
      }
      // Skip on embed routes — those are unauthenticated by design and render
      // their own error state when the demo PAT is missing or invalid.
      if (window.location.pathname.startsWith('/embed/')) {
        return;
      }

      redirecting = true;
      // Lazy import to avoid circular dependency (auth store imports api)
      void import('@/stores/auth')
        .then(({ useAuthStore }) => {
          useAuthStore.getState().clearAuth();
        })
        .catch(() => {});
      toast.error('Session expired — please log in again');
      setTimeout(() => {
        window.location.replace('/login');
      }, 100);
    }
  },
});
